import type { CompositeMark } from "vega-lite/types_unstable/compositemark/index.js";
import type { Mark } from "vega-lite/types_unstable/mark.js";
import { isObject } from "./json.js";

// Vega-Lite's compiler meets an unknown mark type with a bare TypeError
// ("Cannot read properties of undefined"), which tells an author — or the AI
// repair round — nothing. Name it, with its path, before compiling.

const MARK_TYPES = [
  "arc",
  "area",
  "bar",
  "circle",
  "geoshape",
  "image",
  "line",
  "point",
  "rect",
  "rule",
  "square",
  "text",
  "tick",
  "trail",
  "boxplot",
  "errorband",
  "errorbar",
] as const satisfies readonly (Mark | CompositeMark)[];

const MARK_TYPE_SET: ReadonlySet<string> = new Set(MARK_TYPES);

/** Keys under which a Vega-Lite view nests other views. */
const VIEW_ARRAYS = ["layer", "concat", "hconcat", "vconcat"] as const;

function unknownMark(path: string, type: string): string {
  return `${path}: unknown mark type "${type}" (use one of ${MARK_TYPES.join(", ")})`;
}

/** Every unknown mark type in a Vega-Lite spec and the views it composes. */
export function findUnknownVegaLiteMarks(view: unknown, path = ""): string[] {
  if (!isObject(view)) return [];
  const problems: string[] = [];
  const { mark } = view;
  if (typeof mark === "string" && !MARK_TYPE_SET.has(mark)) {
    problems.push(unknownMark(`${path}/mark`, mark));
  } else if (
    isObject(mark) &&
    typeof mark.type === "string" &&
    !MARK_TYPE_SET.has(mark.type)
  ) {
    problems.push(unknownMark(`${path}/mark/type`, mark.type));
  }
  for (const key of VIEW_ARRAYS) {
    const views = view[key];
    if (!Array.isArray(views)) continue;
    views.forEach((child: unknown, i) =>
      problems.push(...findUnknownVegaLiteMarks(child, `${path}/${key}/${i}`)),
    );
  }
  // Facet and repeat wrap a single view in `spec`.
  if (isObject(view.spec)) {
    problems.push(...findUnknownVegaLiteMarks(view.spec, `${path}/spec`));
  }
  return problems;
}
